import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  DocumentDraftDetail,
  DocumentDraftStatus,
  DocumentDraftSummary,
  PendingTransitionView,
  StrategySummary,
} from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import EmailReviewer from "@/components/EmailReviewer";
import { EmailStatusBadge } from "@/components/EmailStatusBadge";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Link } from "react-router-dom";
import type { EmailSummary } from "@/lib/types";

const STATUSES: DocumentDraftStatus[] = [
  "PENDING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "SENT",
];

type QueueTab = "emails" | "documents" | "transitions";

/**
 * Everything waiting on a human, in one place (SA 3.10).
 *
 * <p>Two kinds of thing queue up here and they are not interchangeable: an
 * email is a message the agent wants to send now, a document is an agreement it
 * has drafted. They have different urgency and different reviewers, so they get
 * separate tabs rather than one merged list that would bury the time-sensitive
 * one. Email leads because it is the one that stalls a live conversation.
 */
export default function ApprovalQueuePage() {
  const [tab, setTab] = useState<QueueTab>("emails");

  const pendingEmailsQ = useQuery({
    queryKey: ["emails-pending"],
    queryFn: () => api.get<EmailSummary[]>("/api/v1/emails/pending-approval"),
    refetchInterval: 60_000,
  });
  const emailCount = pendingEmailsQ.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Approval queue</h1>
        <p className="text-sm text-[var(--fg-muted)]">
          Everything the agent has prepared and is waiting for you to release.
        </p>
      </header>

      <div className="flex gap-1 border-b border-[var(--border)]">
        <TabButton active={tab === "emails"} onClick={() => setTab("emails")}>
          Emails
          {emailCount > 0 ? (
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
              {emailCount}
            </span>
          ) : null}
        </TabButton>
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")}>
          Documents
        </TabButton>
        <TabButton active={tab === "transitions"} onClick={() => setTab("transitions")}>
          Phase moves
        </TabButton>
      </div>

      {tab === "emails" ? (
        <EmailApprovals />
      ) : tab === "documents" ? (
        <DocumentApprovals />
      ) : (
        <TransitionApprovals />
      )}
    </div>
  );
}

/**
 * Phase transitions whose rule declared {@code requires_human_approval} —
 * the prospect stays put until someone decides here (SA 2.17 / 3.9).
 */
function TransitionApprovals() {
  const queryClient = useQueryClient();
  const [strategyId, setStrategyId] = useState<string | null>(null);

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });
  useEffect(() => {
    if (strategyId) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategyId(first.id);
  }, [strategiesQ.data, strategyId]);

  const listQ = useQuery({
    queryKey: ["pending-transitions", strategyId],
    enabled: !!strategyId,
    refetchInterval: 60_000,
    queryFn: () =>
      api.get<PendingTransitionView[]>(`/api/v1/transitions?strategyId=${strategyId}`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["pending-transitions", strategyId] });
  };
  const approveM = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/transitions/${id}/approve`),
    onSuccess: invalidate,
  });
  const rejectM = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/transitions/${id}/reject`, {}),
    onSuccess: invalidate,
  });

  const rows = listQ.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm text-[var(--fg-muted)]" htmlFor="transition-strategy">
          Strategy
        </label>
        <select
          id="transition-strategy"
          value={strategyId ?? ""}
          onChange={(e) => setStrategyId(e.target.value)}
          className="rounded-md border border-[var(--border)] bg-[var(--bg-card)] px-2 py-1 text-sm"
        >
          {(strategiesQ.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardTitle>
          {listQ.isLoading ? "Loading…" : `${rows.length} phase move${rows.length === 1 ? "" : "s"} awaiting approval`}
        </CardTitle>
        {rows.length === 0 && !listQ.isLoading ? (
          <p className="text-sm text-[var(--fg-muted)]">
            Nothing waiting. A move shows up here when a transition rule with
            “requires human approval” fires; the prospect stays in its current
            phase until you decide.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-[var(--border)]">
            {rows.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/prospects/${t.prospectId}`}
                    className="font-medium hover:underline"
                  >
                    {t.prospectFullName ?? t.prospectEmail ?? t.prospectId}
                  </Link>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {t.fromPhaseName ?? t.fromPhaseSlug ?? "?"} →{" "}
                    <span className="font-medium text-[var(--fg)]">
                      {t.toPhaseName ?? t.toPhaseSlug ?? "?"}
                    </span>
                    {t.triggerCondition ? ` · rule: ${t.triggerCondition}` : null}
                    {t.setStatus ? ` · sets status ${t.setStatus}` : null}
                    {" · "}
                    {formatDateTime(t.createdAt)}
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    disabled={approveM.isPending || rejectM.isPending}
                    onClick={() => approveM.mutate(t.id)}
                    className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={approveM.isPending || rejectM.isPending}
                    onClick={() => rejectM.mutate(t.id)}
                    className="rounded-md border border-[var(--border)] px-3 py-1 text-xs hover:bg-zinc-50 disabled:opacity-50"
                  >
                    Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {approveM.error || rejectM.error ? (
          <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
            The decision could not be applied — the prospect may have moved on
            since this was queued. Refresh and try again.
          </p>
        ) : null}
      </Card>
    </div>
  );
}

function TabButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mb-px border-b-2 px-4 py-2 text-sm",
        active
          ? "border-[var(--primary)] font-medium text-zinc-900"
          : "border-transparent text-zinc-600 hover:text-zinc-900",
      )}
    >
      {children}
    </button>
  );
}

/** Agent-composed messages awaiting release, across every strategy. */
function EmailApprovals() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listQ = useQuery({
    queryKey: ["emails-pending"],
    queryFn: () => api.get<EmailSummary[]>("/api/v1/emails/pending-approval"),
    refetchInterval: 60_000,
  });

  const items = listQ.data ?? [];

  // Keep the selection valid as the queue drains: once a message is approved it
  // leaves the list, and holding a stale id would show an empty pane.
  useEffect(() => {
    if (selectedId && !items.some((m) => m.id === selectedId)) setSelectedId(null);
    const first = items[0];
    if (!selectedId && first) setSelectedId(first.id);
  }, [items, selectedId]);

  if (listQ.isLoading) return <div className="text-sm text-zinc-500">Loading…</div>;

  if (items.length === 0) {
    return (
      <Card>
        <div className="text-sm text-[var(--fg-muted)]">
          Nothing is waiting for approval. Messages appear here when a strategy or
          prospect is set to review mode and the agent composes one.
        </div>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[22rem_1fr] gap-4 min-h-[24rem]">
      <Card>
        <CardTitle>{items.length} awaiting approval</CardTitle>
        <ul className="divide-y divide-[var(--border)]">
          {items.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => setSelectedId(m.id)}
                className={cn(
                  "w-full rounded-md p-3 text-left hover:bg-zinc-50",
                  selectedId === m.id && "bg-zinc-100",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{m.subject}</span>
                  <EmailStatusBadge status={m.status} />
                </div>
                <div className="mt-0.5 truncate text-xs text-[var(--fg-muted)]">
                  {m.prospectName ?? m.prospectEmail}
                  {m.phaseSlug ? ` · ${m.phaseSlug}` : ""} · {formatDateTime(m.createdAt)}
                </div>
                <div className="mt-1 truncate text-xs text-zinc-600">{m.bodyPreview}</div>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        {selectedId ? (
          <>
            <EmailReviewer emailId={selectedId} onActioned={() => setSelectedId(null)} />
            <div className="mt-3 border-t border-[var(--border)] pt-3">
              <Link
                to={`/prospects/${items.find((m) => m.id === selectedId)?.prospectId ?? ""}`}
                className="text-sm text-[var(--primary)] hover:underline"
              >
                Open this prospect →
              </Link>
            </div>
          </>
        ) : (
          <div className="text-sm text-[var(--fg-muted)]">Select a message to review.</div>
        )}
      </Card>
    </div>
  );
}

function DocumentApprovals() {
  const queryClient = useQueryClient();

  const [strategyId, setStrategyId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] =
    useState<DocumentDraftStatus>("PENDING_APPROVAL");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  useEffect(() => {
    if (strategyId) return;
    const first = strategiesQ.data?.[0];
    if (first) setStrategyId(first.id);
  }, [strategiesQ.data, strategyId]);

  const listQ = useQuery({
    queryKey: ["agreement-drafts", strategyId, statusFilter],
    enabled: !!strategyId,
    queryFn: () =>
      api.get<DocumentDraftSummary[]>(
        `/api/v1/agreements/drafts?strategyId=${strategyId}&status=${statusFilter}`,
      ),
  });

  const detailQ = useQuery({
    queryKey: ["agreement-draft", selectedId],
    enabled: !!selectedId,
    queryFn: () =>
      api.get<DocumentDraftDetail>(`/api/v1/agreements/drafts/${selectedId}`),
  });

  const approveM = useMutation({
    mutationFn: (id: string) =>
      api.post<DocumentDraftDetail>(`/api/v1/agreements/drafts/${id}/approve`),
    onSuccess: () => invalidateAll(queryClient, strategyId, selectedId),
  });

  const rejectM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<DocumentDraftDetail>(`/api/v1/agreements/drafts/${id}/reject`, {
        reason,
      }),
    onSuccess: () => invalidateAll(queryClient, strategyId, selectedId),
  });

  const sendM = useMutation({
    mutationFn: (id: string) =>
      api.post<DocumentDraftDetail>(`/api/v1/agreements/drafts/${id}/send`),
    onSuccess: () => invalidateAll(queryClient, strategyId, selectedId),
  });

  const editM = useMutation({
    mutationFn: ({ id, bodyText }: { id: string; bodyText: string }) =>
      api.patch<DocumentDraftDetail>(
        `/api/v1/agreements/drafts/${id}/body`,
        { bodyText },
      ),
    onSuccess: () => invalidateAll(queryClient, strategyId, selectedId),
  });

  const drafts = listQ.data ?? [];
  const selected = detailQ.data;

  return (
    <div className="space-y-6">
      <p className="text-sm text-[var(--fg-muted)]">
        Review AI-generated draft documents before they are sent. Edit the body,
        approve, reject with a reason, or send to the prospect.
      </p>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Strategy</div>
          <select
            value={strategyId ?? ""}
            onChange={(e) => {
              setStrategyId(e.target.value || null);
              setSelectedId(null);
            }}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white min-w-[16rem]"
            disabled={strategiesQ.isLoading || !strategiesQ.data?.length}
          >
            {strategiesQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.slug})
              </option>
            ))}
            {!strategiesQ.data?.length ? <option value="">No strategies</option> : null}
          </select>
        </label>
        <label className="text-sm">
          <div className="text-xs text-[var(--fg-muted)] mb-1">Status</div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as DocumentDraftStatus);
              setSelectedId(null);
            }}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm bg-white"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <div className="text-xs text-[var(--fg-muted)] pb-1.5">
          {drafts.length} draft{drafts.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid grid-cols-[20rem_1fr] gap-4 min-h-[24rem]">
        <Card>
          <CardTitle>Drafts</CardTitle>
          {listQ.isLoading ? (
            <div className="text-sm text-[var(--fg-muted)]">Loading…</div>
          ) : drafts.length === 0 ? (
            <div className="text-sm text-[var(--fg-muted)]">
              No drafts in {statusFilter}.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {drafts.map((d) => (
                <li
                  key={d.id}
                  onClick={() => setSelectedId(d.id)}
                  className={`py-2 px-2 -mx-2 cursor-pointer rounded-md ${
                    selectedId === d.id
                      ? "bg-zinc-100"
                      : "hover:bg-zinc-50"
                  }`}
                >
                  <div className="text-sm font-medium truncate">
                    {d.templateName ?? d.templateSlug}
                  </div>
                  <div className="text-xs text-[var(--fg-muted)] truncate">
                    {d.prospectFullName ?? d.prospectEmail ?? d.prospectId}
                  </div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {formatDateTime(d.createdAt)} · {d.bodyLength} chars
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          {!selected ? (
            <div className="text-sm text-[var(--fg-muted)]">
              Select a draft on the left to review it.
            </div>
          ) : (
            <DraftReviewer
              draft={selected}
              onApprove={() => approveM.mutate(selected.id)}
              onReject={(reason) => rejectM.mutate({ id: selected.id, reason })}
              onSend={() => sendM.mutate(selected.id)}
              onSaveBody={(bodyText) =>
                editM.mutate({ id: selected.id, bodyText })
              }
              busy={
                approveM.isPending ||
                rejectM.isPending ||
                sendM.isPending ||
                editM.isPending
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}

interface DraftReviewerProps {
  draft: DocumentDraftDetail;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onSend: () => void;
  onSaveBody: (bodyText: string) => void;
  busy: boolean;
}

function DraftReviewer({
  draft,
  onApprove,
  onReject,
  onSend,
  onSaveBody,
  busy,
}: DraftReviewerProps) {
  const [bodyText, setBodyText] = useState(draft.bodyText);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    setBodyText(draft.bodyText);
    setRejectReason("");
  }, [draft.id, draft.bodyText]);

  const dirty = useMemo(
    () => bodyText !== draft.bodyText,
    [bodyText, draft.bodyText],
  );
  const editable = draft.status === "PENDING_APPROVAL";
  const canApprove = draft.status === "PENDING_APPROVAL";
  const canReject = draft.status === "PENDING_APPROVAL";
  const canSend = draft.status === "APPROVED";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <CardTitle>{draft.templateName ?? draft.templateSlug ?? "(no template)"}</CardTitle>
        <StatusPill status={draft.status} />
      </div>
      <div className="text-xs text-[var(--fg-muted)]">
        Prospect: {draft.prospectFullName ?? draft.prospectEmail ?? draft.prospectId}
        {" · created "}
        {formatDateTime(draft.createdAt)}
        {draft.approvedAt
          ? ` · approved ${formatDateTime(draft.approvedAt)}`
          : null}
        {draft.rejectionReason
          ? ` · rejected: ${draft.rejectionReason}`
          : null}
      </div>

      <textarea
        value={bodyText}
        onChange={(e) => setBodyText(e.target.value)}
        disabled={!editable || busy}
        rows={20}
        className="w-full rounded-md border border-[var(--border)] px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-zinc-50 disabled:text-zinc-500"
      />

      {editable ? (
        <div className="flex gap-2 items-center">
          <button
            type="button"
            disabled={!dirty || busy}
            onClick={() => onSaveBody(bodyText)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            Save edits
          </button>
          {dirty ? (
            <span className="text-xs text-[var(--fg-muted)]">Unsaved changes</span>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--border)]">
        <button
          type="button"
          disabled={!canApprove || busy || dirty}
          onClick={onApprove}
          className="rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          Approve
        </button>
        <button
          type="button"
          disabled={!canSend || busy}
          onClick={() => {
            if (
              window.confirm(
                "Send this document to the prospect? A PDF will be rendered and dispatched as a new outbound email.",
              )
            ) {
              onSend();
            }
          }}
          className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          Send
        </button>
        {canReject ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reject reason"
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm min-w-[16rem]"
            />
            <button
              type="button"
              disabled={!rejectReason.trim() || busy}
              onClick={() => onReject(rejectReason.trim())}
              className="rounded-md border border-rose-300 text-rose-700 px-3 py-1.5 text-sm hover:bg-rose-50 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: DocumentDraftStatus }) {
  const tone =
    status === "PENDING_APPROVAL"
      ? "bg-amber-100 text-amber-800"
      : status === "APPROVED"
        ? "bg-emerald-100 text-emerald-700"
        : status === "REJECTED"
          ? "bg-rose-100 text-rose-700"
          : status === "SENT"
            ? "bg-blue-100 text-blue-700"
            : "bg-zinc-100 text-zinc-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {status}
    </span>
  );
}

function invalidateAll(
  queryClient: ReturnType<typeof useQueryClient>,
  strategyId: string | null,
  selectedId: string | null,
) {
  queryClient.invalidateQueries({
    queryKey: ["agreement-drafts", strategyId],
  });
  if (selectedId) {
    queryClient.invalidateQueries({
      queryKey: ["agreement-draft", selectedId],
    });
  }
}
